// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Button, Search, TextArea } from '@carbon/react';
import { useLog } from '../../hooks/useAlarms';
import { addLogNote } from '../../services/opsDataMockService';
import { PageHead, Loading } from '../../components/shell/ScreenHelpers';
import { useSiteTime } from '../../hooks/useSiteTime';
import { useUrlState } from '../../hooks/useUrlState';
import './LogScreen.css';

export default function LogScreen() {
  const { format } = useSiteTime();
  // The search is in the URL (`?q=`) so a refresh keeps the filtered log.
  const [{ q: query }, patchUrl] = useUrlState({ q: '' });
  const [input, setInput] = useState('');
  const { notes, loading, refetch } = useLog(query);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    await addLogNote({
      t: format(new Date(), 'clock'),
      who: 'You',
      siteLabel: 'GA-10 Atlanta',
      text,
    });
    setInput('');
    refetch();
  };

  return (
    <>
      <PageHead title="Notes" dateline="Shift record · GA-10 Atlanta" />

      <div style={{ marginBottom: 16 }}>
        <Search
          id="log-search"
          size="lg"
          labelText="Search log"
          placeholder="Search shift notes…"
          value={query}
          onChange={e => patchUrl({ q: e.target.value })}
          onClear={() => patchUrl({ q: null })}
        />
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="log-notes">
          {notes.map(n => (
            <div
              key={n.id}
              className="hs-card"
              style={{ cursor: 'default' }}
              data-testid="log-note"
              data-note-id={n.id}
            >
              <div className="hs-card-row">
                <span
                  style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: 11,
                    color: 'var(--text-helper)',
                  }}
                >
                  {n.t}
                </span>
                <span style={{ color: 'var(--helios)', fontSize: 12, fontWeight: 500 }}>
                  {n.who}
                </span>
                {(n.asset || n.siteLabel) && (
                  <span className="hs-card-meta" style={{ marginLeft: 'auto' }}>
                    {n.asset && <span>{n.asset}</span>}
                    {n.siteLabel && <span>{n.siteLabel}</span>}
                  </span>
                )}
              </div>
              <div className="hs-card-summary" data-testid="log-summary">
                {n.text}
              </div>
            </div>
          ))}
        </div>
      )}

      <form className="log-composer" onSubmit={handleSubmit}>
        <TextArea
          id="log-note"
          labelText="New note"
          placeholder="Add a shift note…"
          rows={2}
          value={input}
          onChange={e => setInput(e.target.value)}
        />
        <Button type="submit" disabled={!input.trim()}>
          Add note
        </Button>
      </form>
    </>
  );
}
